/* eslint-disable no-unused-vars */
import {WalletPlatform} from '@/enums/WalletPlatform'
import {BadInvocationRequest} from '@/model/wallets/types/WalletErrors'
import {isWalletConnectProvider} from '../connection/WalletConnectContext'
import {
  getWalletConnectAddress,
  resolveWalletConnectInvoke,
} from '../connection/WalletConnectConnection'
import {
  isNeolineExtensionProvider,
  NeolineExtensionProviderContext,
} from '../connection/NeolineExtensionConnection'
import {
  isNeoLineNep21Provider,
  NeoLineNep21ProviderContext,
} from '../connection/NeoLineConnection'
import {isNeolineNeoXEthereumProvider} from '../connection/NeolineNeoXEthereumConnection'
import {WalletProviderStrategy} from './WalletProviderStrategy'

const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value))

/** NeoLine dAPI expects signer `account` as script hash without `0x` prefix. */
const normalizeNeolineSignerAccount = (account: unknown) => {
  if (typeof account !== 'string' || !account) {
    return account
  }

  return account.startsWith('0x') ? account.slice(2) : account
}

const normalizeNeolineSigners = (signers: unknown) => {
  if (!Array.isArray(signers)) {
    return signers
  }

  return signers.map(signer => {
    if (!signer || typeof signer !== 'object') {
      return signer
    }

    const entry = signer as Record<string, unknown>

    return {
      ...entry,
      account: normalizeNeolineSignerAccount(entry.account),
    }
  })
}

const multiPayloadFields = (payload: Record<string, unknown>) => ({
  fee: payload.fee,
  extraSystemFee: payload.extraSystemFee,
  overrideSystemFee: payload.overrideSystemFee,
  broadcastOverride: payload.broadcastOverride,
  signers: normalizeNeolineSigners(cloneJson(payload.signers)),
})

/** Neoline N3 extension dAPI (`invokeMultiple`) */
const toExtensionMultiPayload = (payload: Record<string, unknown>) => {
  const invokeArgs = cloneJson(payload.invocations ?? payload.invokeArgs)

  if (!Array.isArray(invokeArgs) || invokeArgs.length === 0) {
    return payload
  }

  return {...multiPayloadFields(payload), invokeArgs}
}

/** NEP-21 multi-invoke (`invokeMulti` / `invoke`) */
const toNep21MultiPayload = (payload: Record<string, unknown>) => {
  const invocations = cloneJson(payload.invocations ?? payload.invokeArgs)

  if (!Array.isArray(invocations) || invocations.length === 0) {
    return payload
  }

  return {...multiPayloadFields(payload), invocations}
}

export class NeoLineProvider implements WalletProviderStrategy {
  platform = WalletPlatform.NEOLINE
  nep21Name = 'NeoLine'

  getAddress(provider: any) {
    if (isWalletConnectProvider(provider)) {
      return getWalletConnectAddress(provider)
    }

    if (isNeolineExtensionProvider(provider)) {
      return provider.address
    }

    if (isNeoLineNep21Provider(provider)) {
      return provider.address
    }

    if (isNeolineNeoXEthereumProvider(provider)) {
      return provider.address
    }

    return provider?.account?.address ?? null
  }

  async invoke(provider: any, payload: any) {
    if (isWalletConnectProvider(provider)) {
      return resolveWalletConnectInvoke(provider, payload)
    }

    if (isNeolineExtensionProvider(provider)) {
      const multiPayload = toExtensionMultiPayload(payload)

      return this.invokeExtension(provider, multiPayload)
    }

    if (isNeoLineNep21Provider(provider)) {
      return this.invokeNep21(provider, payload)
    }

    if (isNeolineNeoXEthereumProvider(provider)) {
      return this.invokeNeoXEthereum(provider, payload)
    }

    return provider.invoke(payload)
  }

  private async invokeExtension(
    provider: NeolineExtensionProviderContext,
    payload: any
  ) {
    const invokeOutput = await provider.sdk.invokeMultiple(payload)
    if (!invokeOutput?.txid) throw BadInvocationRequest

    return invokeOutput.txid
  }

  private async invokeNep21(
    provider: NeoLineNep21ProviderContext,
    payload: any
  ) {
    const neoLineN3 = (window as any).NEOLineN3

    if (typeof neoLineN3 !== 'undefined') {
      const sdk = new neoLineN3.Init()
      const extensionPayload = toExtensionMultiPayload(payload)
      const invokeOutput = await sdk.invokeMultiple(extensionPayload)

      if (invokeOutput?.txid) return invokeOutput.txid
    }

    const nep21 = provider.provider
    const nep21Payload = toNep21MultiPayload(payload)

    for (const method of [
      'invokeFunction',
      'invokeMulti',
      'invokeMultiple',
    ] as const) {
      if (typeof nep21[method] !== 'function') continue

      const result = await nep21[method](nep21Payload)
      if (result?.txid) return result.txid
    }

    if (typeof nep21.invoke === 'function') {
      const result = await nep21.invoke(nep21Payload)
      if (typeof result === 'string') return result
      if (result?.txid) return result.txid
    }

    throw BadInvocationRequest
  }

  private async invokeNeoXEthereum(
    provider: {
      provider: {
        request: (args: {
          method: string
          params?: unknown[]
        }) => Promise<unknown>
      }
    },
    payload: Record<string, unknown>
  ) {
    const txHash = await provider.provider.request({
      method: 'eth_sendTransaction',
      params: [payload],
    })

    if (typeof txHash !== 'string' || !txHash) throw BadInvocationRequest

    return txHash
  }
}
